import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { TextField } from "@mui/material";

interface OtpInputProps {
  value: string;
  numInputs?: number;
  onChange: (otp: string) => void;
  isDisabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  showInput?: boolean;
}

export type OtpInputHandle = {
  focusInput: (index: number) => void;
};

const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>((props, ref) => {
  const {
    numInputs = 6,
    onChange,
    value = "",
    isDisabled = false,
    hasError = false,
    autoFocus = true,
    placeholder = "",
    className = "",
    showInput = false,
  } = props;

  const [_activeInput, setActiveInput] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useImperativeHandle(ref, () => ({
    focusInput: (index: number) => {
      focusInput(index);
    },
  }));

  const focusInput = (index: number) => {
    const activeIndex = Math.max(Math.min(numInputs - 1, index), 0);
    inputRefs.current[activeIndex]?.focus();
    setActiveInput(activeIndex);
  };

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value;
    if (newValue.length <= 1 && /^\d*$/.test(newValue)) {
      const otpArray = value.split("");
      otpArray[index] = newValue;
      onChange(otpArray.join(""));

      if (newValue && index < numInputs - 1) {
        focusInput(index + 1);
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    const allowedKeys = [
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(e.key)) {
      e.preventDefault();
      const otpArray = value.split("");

      switch (e.key) {
        case "Backspace":
          if (!otpArray[index] && index > 0) {
            otpArray[index - 1] = "";
            onChange(otpArray.join(""));
            focusInput(index - 1);
          } else {
            otpArray[index] = "";
            onChange(otpArray.join(""));
          }
          break;

        case "ArrowLeft":
          focusInput(index - 1);
          break;

        case "ArrowRight":
          focusInput(index + 1);
          break;

        case "Delete":
          otpArray[index] = "";
          onChange(otpArray.join(""));
          break;

        case "Home":
          focusInput(0);
          break;

        case "End":
          focusInput(numInputs - 1);
          break;
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text/plain")
      .slice(0, numInputs);
    if (/^\d+$/.test(pastedData)) {
      onChange(pastedData.padEnd(numInputs, ""));
    }
  };

  useEffect(() => {
    if (autoFocus) {
      focusInput(0);
    }
  }, []);

  return (
    <div className={className}>
      {Array.from({ length: numInputs }, (_, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: "center",
              fontSize: "1.5rem",
              padding: "8px",
              width: "100%",
            },
          }}
          variant="outlined"
          size="small"
          className="otp-input"
          placeholder={placeholder}
          value={value[index] || ""}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={isDisabled}
          error={hasError}
          type={showInput ? "text" : "password"}
        />
      ))}
    </div>
  );
});

export { OtpInput };
